import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { waGeneral } from "@/lib/whatsapp";

const links = [
    { to: "/", label: "Home", num: "01" },
    { to: "/products", label: "Products", num: "02" },
    { to: "/catalogue", label: "Catalogue", num: "03" },
    { to: "/about", label: "About", num: "04" },
    { to: "/contact", label: "Contact", num: "05" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <>
            <motion.header
                initial={{ y: -80 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: [0.77, 0, 0.175, 1] }}
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-bone/90 backdrop-blur-xl border-b border-ink/10 shadow-[0_1px_0_0_rgba(0,0,0,0.03)]"
                        : "bg-bone/60 backdrop-blur-md"
                }`}
                data-testid="site-nav"
            >
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
                        <img src="/assets/logo.png" alt="Mist Paints" className="h-8 md:h-9 w-auto" />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `group relative px-4 py-2 text-sm tracking-wide transition-colors ${
                                        isActive ? "text-ink" : "text-ink/70 hover:text-ink"
                                    }`
                                }
                                data-testid={`nav-${l.label.toLowerCase()}`}
                            >
                                {({ isActive }) => (
                                    <span className="flex items-center gap-2">
                                        <span className="font-mono text-[10px] text-ink/40">{l.num}</span>
                                        <span>{l.label}</span>
                                        {isActive && (
                                            <motion.span
                                                layoutId="nav-dot"
                                                className="absolute -bottom-0.5 left-4 right-4 h-px bg-brand-red"
                                            />
                                        )}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            href={waGeneral()}
                            target="_blank"
                            rel="noreferrer"
                            className="hidden md:inline-flex btn-pill bg-ink text-bone hover:bg-brand-red"
                            data-testid="nav-whatsapp-cta"
                        >
                            <span className="w-2 h-2 rounded-full bg-brand-yellow animate-pulse" />
                            Chat on WhatsApp
                        </a>
                        <button
                            onClick={() => setOpen(true)}
                            className="md:hidden p-2 -mr-2"
                            aria-label="Open menu"
                            data-testid="nav-menu-open"
                        >
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-ink text-bone md:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex items-center justify-between h-16 px-6 border-b border-bone/10">
                            <img src="/assets/logo.png" alt="Mist Paints" className="h-8 w-auto" />
                            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-menu-close">
                                <X size={22} />
                            </button>
                        </div>
                        <div className="px-6 py-10 flex flex-col gap-2">
                            {links.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 + i * 0.06 }}
                                >
                                    <Link
                                        to={l.to}
                                        className="flex items-baseline justify-between py-4 border-b border-bone/10"
                                        data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                    >
                                        <span className="font-display text-4xl">{l.label}</span>
                                        <span className="font-mono text-xs opacity-50">{l.num}</span>
                                    </Link>
                                </motion.div>
                            ))}
                            <a
                                href={waGeneral()}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-8 btn-pill bg-brand-red text-bone justify-center"
                                data-testid="mobile-whatsapp-cta"
                            >
                                Chat on WhatsApp
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
